/**
 * Prepares a photo of an ID card for upload.
 *
 * Almost every customer here is on a phone, so the file arriving is a 5-8 MB camera shot on
 * mobile data. The long edge is capped rather than the file size, which keeps the numbers on
 * a card legible while cutting the upload to a fraction of the original.
 */

/** Only photos: an ID is a picture, and a PDF in this field is a customer misreading it. */
export const IDENTITY_ACCEPT = 'image/*'

export const MAX_IDENTITY_BYTES = 10 * 1024 * 1024

/** For the copy that states the limit, so the number cannot drift from the check. */
export const MAX_IDENTITY_MB = MAX_IDENTITY_BYTES / (1024 * 1024)

const MAX_EDGE = 1600
const JPEG_QUALITY = 0.85
const DECODE_TIMEOUT_MS = 4000

export type IdentityImageErrorReason = 'too-large' | 'not-image' | 'unreadable'

export type PreparedIdentityImage = {
  dataUrl: string
  fileName: string
}

export class IdentityImageError extends Error {
  readonly reason: IdentityImageErrorReason

  constructor(reason: IdentityImageErrorReason) {
    super(reason)
    this.name = 'IdentityImageError'
    this.reason = reason
  }
}

/** The `rent` translation key explaining a failed pick. */
export function identityImageErrorKey(error: unknown): string {
  const reason = error instanceof IdentityImageError ? error.reason : 'unreadable'
  if (reason === 'too-large') return 'identity.errorTooLarge'
  if (reason === 'not-image') return 'identity.errorNotImage'
  return 'identity.errorUnreadable'
}

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(new IdentityImageError('unreadable'))
    reader.readAsDataURL(file)
  })

/**
 * Resolves null when the image cannot be decoded, including the case where the browser
 * fires neither event: some in-app browsers go quiet on a large data URL, and a customer
 * must not be left holding a button that never comes back.
 */
const loadImage = (dataUrl: string): Promise<HTMLImageElement | null> =>
  new Promise((resolve) => {
    const image = new Image()
    const timer = setTimeout(() => resolve(null), DECODE_TIMEOUT_MS)
    const settle = (result: HTMLImageElement | null) => {
      clearTimeout(timer)
      resolve(result)
    }
    image.onload = () => settle(image)
    image.onerror = () => settle(null)
    image.src = dataUrl
  })

/** Downscaling re-encodes as JPEG, so the name has to follow. */
const toJpegName = (fileName: string): string => {
  const base = fileName.replace(/\.[^.]+$/, '') || 'identity'
  return `${base}.jpg`
}

/**
 * Caps the long edge at MAX_EDGE. Hands back the original when it already fits or when the
 * canvas is unavailable, so a usable photo is never lost to a failed optimisation.
 */
export async function downscaleImage(dataUrl: string, fileName: string): Promise<PreparedIdentityImage> {
  const image = await loadImage(dataUrl)
  if (!image) return { dataUrl, fileName }

  const width = image.naturalWidth || 0
  const height = image.naturalHeight || 0
  const longEdge = Math.max(width, height)
  if (!longEdge || longEdge <= MAX_EDGE) return { dataUrl, fileName }

  const scale = MAX_EDGE / longEdge
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(width * scale)
  canvas.height = Math.round(height * scale)

  const ctx = canvas.getContext('2d')
  if (!ctx) return { dataUrl, fileName }

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height)
  const resized = canvas.toDataURL('image/jpeg', JPEG_QUALITY)
  if (!resized || !resized.startsWith('data:image')) return { dataUrl, fileName }

  return { dataUrl: resized, fileName: toJpegName(fileName) }
}

export async function readIdentityImage(file: File): Promise<PreparedIdentityImage> {
  // Checked before reading: a phone can hand over a video from the gallery, and there is no
  // point loading 200 MB of it into memory to find out.
  if (file.type && !file.type.startsWith('image/')) {
    throw new IdentityImageError('not-image')
  }
  if (file.size > MAX_IDENTITY_BYTES) {
    throw new IdentityImageError('too-large')
  }

  const dataUrl = await readAsDataUrl(file)
  if (!dataUrl.startsWith('data:image')) {
    throw new IdentityImageError('not-image')
  }

  return downscaleImage(dataUrl, file.name)
}
